import type { GridColDef } from '@mui/x-data-grid'
import { CheckSettingsRow, MultiEditCheckSettingsElement } from '../MultiEditCheckSettings/types'

/**
 * True when the member check settings carry a populated vibration check
 * settings object, i.e. the member is a slab/roof-slab beam whose vibration
 * checks have been derived from its assembly. Plain timber and steel members
 * never carry this field; a timber-slab member may still have it unset before
 * that derivation has run.
 */
export const hasVibrationCheckSettings = (
  settings: SettingsOnMember | undefined,
): settings is TimberSlabCheckSettings & { vibration_check_settings: VibrationCheckSettings } =>
  settings?.setting_type === 'timber-slab' && !!settings.vibration_check_settings

export const getVibrationCheckSettings = (
  settings: SettingsOnMember | undefined,
): VibrationCheckSettings | undefined =>
  settings && 'vibration_check_settings' in settings ? settings.vibration_check_settings : undefined

/**
 * The only field the Schwingung form edits. Every other field of
 * VibrationCheckSettings (slab_weight_per_area, step_size, screed_data, …) is
 * derived per member from its own assembly and must never be copied across
 * members — see {@link mergeVibrationCheckSettings}.
 */
export const getVibrationSlabWidth = (settings: SettingsOnMember | undefined): number | undefined =>
  getVibrationCheckSettings(settings)?.slab_width

/**
 * The staged settings are always seeded from a member that already passed
 * {@link hasVibrationCheckSettings} (see SharedSettingsFields), so a populated
 * vibration_check_settings object is guaranteed here — unlike hot dimensioning
 * or shear check, there is no safe default to synthesize for the other fields
 * (a slab weight or screed makeup invented out of thin air would be wrong),
 * so this is an identity function kept for interface parity with the other
 * sections.
 */
export const normalizeVibrationCheckSettings = (settings: SettingsOnMember): SettingsOnMember =>
  settings

/**
 * Applies the staged shared slab_width to one member, leaving every other
 * field of its vibration_check_settings — and the rest of its check settings —
 * untouched. Unlike hot dimensioning/shear check, the whole shared object must
 * NOT be stamped onto the member: slab_weight_per_area, step_size, screed_data
 * etc. are derived from that member's own assembly and copying another
 * member's values would silently corrupt its vibration check.
 */
export const mergeVibrationCheckSettings = (
  existing: SettingsOnMember,
  shared: SettingsOnMember,
): SettingsOnMember => {
  const existingVibration = getVibrationCheckSettings(existing)
  const sharedSlabWidth = getVibrationSlabWidth(shared)

  if (!existingVibration || sharedSlabWidth == null) return existing

  return {
    ...existing,
    vibration_check_settings: { ...existingVibration, slab_width: sharedSlabWidth },
  } as SettingsOnMember
}

export const vibrationSettingsColumns: GridColDef<CheckSettingsRow>[] = [
  { field: 'slabWidth', headerName: 'Deckenbreite', flex: 1, minWidth: 100 },
]

const slabWidthNumberFormatter = new Intl.NumberFormat('de-DE', {
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
})

/** While a shared value is staged it is previewed for every row. */
export const buildVibrationSettingsRow = (
  element: MultiEditCheckSettingsElement,
  sharedSettings: SettingsOnMember | undefined,
): Record<string, string> => {
  const slabWidth =
    getVibrationSlabWidth(sharedSettings) ?? getVibrationSlabWidth(element.checkSettings)

  return {
    slabWidth: slabWidth != null ? `${slabWidthNumberFormatter.format(slabWidth)} m` : '–',
  }
}
